'use client';

import {
  Bug01Icon,
  Globe02Icon,
  Radar01Icon,
  SecurityCheckIcon,
  Upload01Icon,
  WorkflowSquare01Icon,
  type TerraIcon,
} from '@terra/ui-icons';
import { Box, HStack, styled, token } from '@terra/ui-styled-system';
import {
  INTEGRATION_TYPES,
  type IntegrationType,
} from '@terra/shared-contracts-integrations';
import {
  isFileImportSource,
  isNativeProductSignalSource,
  type SignalSource,
} from '@terra/shared-contracts-signals';
import { MetaDefault, Tooltip, useBranding } from '../..';

const ProviderImage = styled('img');

const NATIVE_ICON_COLOR = token('colors.fg.accent.default');
const THIRD_PARTY_ICON_COLOR = token('colors.fg.neutral.default');
const TerraMarkIcon = ({ size, color }: { size: number; color: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.11422 2.11838C5.39241 1.44677 5.53559 0.726944 5.53559 0H10.3375C10.3375 1.35755 10.0702 2.7018 9.55065 3.95601C9.34898 4.44289 9.11084 4.91249 8.83879 5.36102C8.75408 5.50069 8.93427 5.69018 9.07803 5.61263C10.5728 4.80621 12.2708 4.38632 14 4.38632V9.18828C12.5319 9.18828 11.1105 9.7581 10.0724 10.7962C9.20286 11.6657 8.65248 12.7947 8.49664 14.0012H3.67341C3.78711 12.347 4.29657 10.753 5.14776 9.3495C5.23247 9.20983 5.05229 9.0203 4.90854 9.09786C4.59951 9.26459 4.28162 9.41577 3.95601 9.55065C2.7018 10.0702 1.35755 10.3375 0 10.3375L7.21695e-07 5.53559C0.726944 5.53559 1.44677 5.39241 2.11838 5.11422C2.78999 4.83603 3.40023 4.42828 3.91425 3.91425C4.42828 3.40022 4.83603 2.78999 5.11422 2.11838Z"
      fill={color}
    />
  </svg>
);

function BrandedProductMark({
  size,
  label,
  logoUrl,
}: {
  size: number;
  label: string;
  logoUrl: string | null;
}) {
  if (logoUrl) {
    return (
      <ProviderImage
        src={logoUrl}
        alt={label}
        flex="none"
        objectFit="contain"
        style={{ width: `${size}px`, height: `${size}px` }}
      />
    );
  }

  const monogram = label.trim().charAt(0).toUpperCase() || '?';

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      flex="none"
      borderRadius="sm"
      bg="bg.neutral.subtle"
      color="fg.muted"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        fontSize: `${Math.round(size * 0.6)}px`,
      }}
    >
      {monogram}
    </Box>
  );
}

const THIRD_PARTY_PROVIDER_ICONS: Partial<Record<IntegrationType, TerraIcon>> =
  {
    [INTEGRATION_TYPES.WIZ]: SecurityCheckIcon,
    [INTEGRATION_TYPES.CROWDSTRIKE]: SecurityCheckIcon,
    [INTEGRATION_TYPES.XPANSE]: Radar01Icon,
    [INTEGRATION_TYPES.HACKERONE]: Bug01Icon,
    [INTEGRATION_TYPES.BUGCROWD]: Bug01Icon,
    [INTEGRATION_TYPES.UPWIND]: SecurityCheckIcon,
    [INTEGRATION_TYPES.JENKINS]: WorkflowSquare01Icon,
  };

export type SignalSourceTagProps = {
  source: SignalSource;
  size?: number;
  /**
   * 'label' shows the source name next to the icon (e.g. the signal detail
   * panel). 'iconOnly' shows just the icon and moves the source name into a
   * hover tooltip (e.g. compact table/card rows).
   */
  variant?: 'label' | 'iconOnly';
};
export const SignalSourceTag = ({
  source,
  size = 14,
  variant = 'label',
}: SignalSourceTagProps) => {
  const { brandName, logoUrl, isWhiteLabeled } = useBranding();
  const brandedName = brandName.trim();
  const brandedLogoUrl = logoUrl?.trim() || null;
  const isNative = source.sourceType === 'native';
  const useBrandedProductSource =
    isWhiteLabeled && isNativeProductSignalSource(source);
  const useBrandedMark = isWhiteLabeled && isNative;
  const displayName =
    useBrandedProductSource && brandedName ? brandedName : source.sourceName;
  const iconColor = isNative ? NATIVE_ICON_COLOR : THIRD_PARTY_ICON_COLOR;
  const ThirdPartyIcon: TerraIcon = isFileImportSource(source)
    ? Upload01Icon
    : (source.provider &&
        THIRD_PARTY_PROVIDER_ICONS[source.provider as IntegrationType]) ||
      Globe02Icon;
  const icon = useBrandedMark ? (
    <BrandedProductMark
      size={size}
      label={brandedName || displayName}
      logoUrl={brandedLogoUrl}
    />
  ) : isNative ? (
    <TerraMarkIcon size={size} color={iconColor} />
  ) : (
    <ThirdPartyIcon size={size} color={iconColor} />
  );

  if (variant === 'iconOnly') {
    return (
      <Tooltip title={displayName}>
        <HStack alignItems="center" minW={0}>
          {icon}
        </HStack>
      </Tooltip>
    );
  }

  return (
    <HStack gap={1.5} alignItems="center" minW={0}>
      {icon}
      <MetaDefault color="fg.default" truncate>
        {displayName}
      </MetaDefault>
    </HStack>
  );
};
